import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Coupons and their usage tracking — mirrors stacks-agents `src/app/api/coupons/`. */
@Injectable({ providedIn: 'root' })
export class CouponsApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getCoupons(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/coupons`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getCoupon(projectId: string, couponId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/coupons/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, couponId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  getCouponUsers(projectId: string, couponId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/coupons/users`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, couponId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  /** Creates a coupon with its product and category links. @returns The new coupon UUID, or null on failure. */
  async insertCoupon(projectId: string, coupon: any): Promise<string | null> {
    const res = await this.http
      .post<{ data: { id: string } }>(`${environment.agentsUrl}v4/creation/coupons`,
        { projectId, coupon },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
    return res?.data?.id ?? null
  }

  /** Increments the per-user usage counter for a coupon (row created on first use). */
  async trackCouponUsage(projectId: string, couponId: string, userId: string): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/coupons/track-usage`,
        { projectId, couponId, userId },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Updates an existing coupon and re-syncs its junction tables. */
  async updateCoupon(projectId: string, couponId: string, coupon: any): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/coupons`,
        { projectId, couponId, coupon },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Increments the global current_uses counter on a coupon. */
  async incrementCouponUses(projectId: string, couponId: string): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/coupons/increment-uses`,
        { projectId, couponId },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a coupon (junction rows cascade). */
  async deleteCoupon(projectId: string, couponId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/coupons`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, couponId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
